import React, { useState } from 'react';
import { useForm, useFormState, Controller } from 'react-hook-form';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useDispatch } from 'react-redux';
import { refund } from 'redux/slices/transaction';
import { yupResolver } from '@hookform/resolvers/yup';

import ErrorMessage from 'components/ErrorMessage';
import PriceInput from 'components/PriceInput';
import Select from 'components/Select';
import ModalWithChildren from 'components/ModalWithChildren';
import LabelWithChildren from 'components/LabelWithChildren';
import { refundPayment as TEXT } from 'locales/en/transaction';
import schema from './RefundPayment.schema';
import { REFUND_REASONS } from './helpers';
import { CURRENCY_SYMBOL } from 'utils/transform';

const RefundPayment = ({ transaction, className }) => {
  const { register, control, handleSubmit, clearErrors } = useForm({
    context: { maxAmount: transaction.refundableAmount },
    resolver: yupResolver(schema),
    defaultValues: {
      amount: transaction.refundableAmount,
      reason: '',
      reason_option: undefined,
    },
  });
  const { errors } = useFormState({ control });

  const dispatch = useDispatch();
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const onClickConfirm = ({ reason_option, reason, amount }) => {
    setIsLoading(true);
    const _reason = reason
      ? `${reason_option.label}: ${reason}`
      : reason_option.label;
    dispatch(refund({ id: transaction.id, amount, reason: _reason })).then(
      () => {
        setIsOpen(false);
        setIsLoading(false);
      }
    );
  };

  const onClickCancel = () => {
    clearErrors();
    setIsOpen(false);
  };

  return (
    <>
      <button className={className} onClick={() => setIsOpen(true)}>
        {TEXT.title}
      </button>
      {isOpen && (
        <ModalWithChildren
          isOpen
          header={TEXT.header}
          isLoading={isLoading}
          ctaTitle={isLoading ? TEXT.refunding : TEXT.title}
          onClickCta={handleSubmit(onClickConfirm)}
          cta2Title={TEXT.cancel}
          onClickCta2={onClickCancel}
        >
          <>
            <div className="mt-12">
              <div>
                <FontAwesomeIcon icon="info-circle" />
                <span className="ml-2 text-sm text-javelin-gray-dark">
                  {TEXT.message}
                </span>
              </div>
            </div>
            <div className="grid gap-y-5 mt-7">
              <PriceInput
                label={
                  <span className="text-xs text-javelin-gray-light w-40">
                    {TEXT.amount}
                  </span>
                }
                className="flex flex-col lg:items-baseline mb-0 lg:flex-row"
                inputClassName="flex-1"
                name="amount"
                control={control}
                inputPrefix={CURRENCY_SYMBOL[transaction.currency]}
                thousandSeparator
                fixedDecimalScale
                decimalScale={2}
                error={errors.amount?.message}
              />
              <LabelWithChildren
                label={
                  <span className="text-xs text-javelin-gray-light w-40">
                    {TEXT.reason}
                  </span>
                }
                className="-mb-2 lg:items-start"
                name="reason"
              >
                <div>
                  <Controller
                    name="reason_option"
                    control={control}
                    render={({ field: { ref, ...otherProps } }) => {
                      return (
                        <Select
                          {...otherProps}
                          placeholder={TEXT.selectReason}
                          className="flex-1"
                          options={REFUND_REASONS}
                          hasError={!!errors.reason_option}
                        />
                      );
                    }}
                  />
                  <textarea
                    className="text-sm rounded-md border-javelin-inputBorder resize-none w-full mt-2 flex-1 placeholder-javelin-placeholder"
                    rows={5}
                    placeholder={TEXT.addMore}
                    {...register('reason')}
                  />
                  {errors.reason_option && (
                    <ErrorMessage
                      message={errors.reason_option.value.message}
                    />
                  )}
                </div>
              </LabelWithChildren>
            </div>
          </>
        </ModalWithChildren>
      )}
    </>
  );
};

RefundPayment.propTypes = {
  transaction: PropTypes.shape({}),
  className: PropTypes.string,
};
RefundPayment.defaultProps = {
  className: 'btn btn-secondary w-40 h-11 px-0',
};

export default RefundPayment;
